import { ParamsType, ProTable } from "@ant-design/pro-components";
import {
  ActionType,
  ProColumns,
  ProTableProps,
} from "@ant-design/pro-table/es/typing";
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FormOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import React, { useMemo, useRef } from "react";
import { Button, ButtonProps, FormInstance } from "antd";
import { AnyObject } from "@triare/auth-redux";
import { useNavigate } from "react-router";
import useCurrentLocation from "../../../hooks/router";
import Tooltip from "../Tooltip";
import { useTranslation } from "react-i18next";

import styles from "./index.module.scss";

export interface TableOnRowSelect {
  params?: Record<string, string>;
  selectedRows?: string[];
  onRowSelection?: ((selectedRows: string[]) => void) | undefined;
}

export interface TableListItem {
  id: string | number;
  key: string | number;
}

/**
 * The narrowest an Actions column may be. Content decides the width above it —
 * a row of icon buttons, or whatever a screen renders instead.
 */
const ACTIONS_MIN_WIDTH = 120;

export interface TableProps<T, U, V> extends ProTableProps<T, U, V> {
  actions?: (
    | [
        (props: {
          text: React.ReactNode;
          record: T;
          id: number;
          className: string;
        }) => React.ReactNode,
      ]
    | [
        (
          props: {
            text: React.ReactNode;
            record: T;
            id: number;
            className: string;
          } & AnyObject,
        ) => React.ReactNode,
        AnyObject,
      ]
    | [
        string,
        (props: {
          text: React.ReactNode;
          record: T;
          id: number;
        }) => ProColumns<T, V>,
      ]
    | [
        string,
        (props: {
          text: React.ReactNode;
          record: T;
          id: number;
        }) => ProColumns<T, V>,
        (props: { text: React.ReactNode; record: T; id: number }) => boolean,
      ]
  )[];
  /** Used to clear filters & table data */
  resetKey: () => void;
}

/**
 * One row action: an icon button that says what it is on hover.
 *
 * The name arrives as `title`, which is where every screen already puts it —
 * but on a Button that is the browser's own tooltip, which cannot be styled,
 * cannot be delayed and looks nothing like the rest of the app. It is taken off
 * the button and given to antd's Tooltip instead, so there is one label rather
 * than two racing each other.
 *
 * The key belongs on the outermost element, since these are rendered in a list.
 */
const rowAction = (key: string, icon: React.ReactNode) => {
  const Action = ({ title, ...props }: ButtonProps) => (
    <Tooltip key={key} title={title}>
      <Button type="text" {...props} icon={icon} />
    </Tooltip>
  );

  Action.displayName = `RowAction(${key})`;

  return Action;
};

export const listAction = {
  form: rowAction("form", <FormOutlined />),
  edit: rowAction("edit", <EditOutlined />),
  delete: rowAction("delete", <DeleteOutlined />),
  reload: rowAction("reload", <ReloadOutlined />),
  copy: rowAction("copy", <CopyOutlined />),
  download: rowAction("download", <DownloadOutlined />),
  view: rowAction("view", <EyeOutlined />),
};

function Table<
  T extends TableListItem,
  Params extends ParamsType = ParamsType,
  ValueType = "text",
>({
  options,
  pagination,
  columns,
  actions,
  form,
  search,
  resetKey,
  ...props
}: TableProps<T, Params, ValueType>) {
  const formRef = useRef<FormInstance>(undefined);
  const actionRef = useRef<ActionType>(undefined);
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const currentLocation = useCurrentLocation();

  /**
   * A row opens the same thing its row action does: view if the row has one,
   * edit otherwise. The handler is taken from the action itself rather than
   * duplicated, so the two can never disagree, and a row that hides its action
   * — a sent campaign cannot be edited — is not clickable either.
   */
  const rowOpen = (record: T, index: number) => {
    if (!actions) {
      return undefined;
    }

    for (const key of ["view", "edit"]) {
      const action = actions.find((item) => item[0] === key);

      if (!action) {
        continue;
      }

      const [, createData, check] = action as [
        string,
        (props: { text: React.ReactNode; record: T; id: number }) => AnyObject,
        ((props: { text: React.ReactNode; record: T; id: number }) => boolean)?,
      ];
      const args = { text: record.id, record, id: index };

      if (check && !check(args)) {
        continue;
      }

      const data = typeof createData === "function" ? createData(args) : null;

      if (typeof data?.onClick === "function") {
        return data.onClick;
      }
    }

    return undefined;
  };

  const newColumns = useMemo(() => {
    const list = (columns || []).map((column) => {
      if (column.hideInSearch === true) {
        return column;
      }

      return column;
      // column.
    });

    if (actions) {
      list.push({
        key: "id",
        title: t("common.table.actions"),
        dataIndex: "id",
        hideInSearch: true,
        // No fixed width: the column takes as much as its buttons need, whether
        // those are three icons or a text link a screen passed in. The floor is
        // 120, so a single icon does not leave a sliver of a column beside a
        // header that is wider than it.
        //
        // min-width rather than width, on the cells themselves: a `width` would
        // fix the column and stop it growing with its content, and antd's own
        // table lays out on `table-layout: auto` unless scroll.x is set, which
        // honours a cell's min-width.
        onHeaderCell: () => ({ style: { minWidth: ACTIONS_MIN_WIDTH } }),
        onCell: () => ({ style: { minWidth: ACTIONS_MIN_WIDTH } }),
        render: (text, record, id) => (
          <div className={styles.actions}>
            {actions
              .map((action) => {
                if (typeof action[0] === "string") {
                  const [key, createData, check] = action;

                  if (
                    (!check || check({ text, record, id })) &&
                    typeof createData === "function"
                  ) {
                    const data = createData({ text, record, id });

                    // @ts-ignore - use the action icon in the list or use a custom element
                    return listAction[key] ? listAction[key](data) : data;
                  }
                } else {
                  const [Component, additionalProps] = action;

                  return (
                    <Component
                      key={Component.name}
                      id={id}
                      text={text}
                      record={record}
                      className={styles.actionBtn}
                      {...additionalProps}
                    />
                  );
                }

                return null;
              })
              .filter((value) => value !== null)}
          </div>
        ),
      });
    }

    return list;
    // TODO upd deps
  }, [i18n.language]);

  return (
    <ProTable<T, Params, ValueType>
      formRef={formRef}
      actionRef={actionRef}
      className={styles.table}
      rowKey="key"
      dateFormatter="string"
      // Every table pages, and every table lets the reader choose how much of
      // it to see at once. antd only shows the page-size select once a list is
      // long enough to warrant it, so it is asked for explicitly here rather
      // than left to that threshold.
      pagination={{
        defaultPageSize: 10, // 20,
        showQuickJumper: true,
        showSizeChanger: true,
        // showTotal: true,
        pageSizeOptions: [10, 20, 30, 50, 100],
        ...pagination,
      }}
      onReset={() => {
        const isSyncToUrl = form?.syncToUrl;

        if (actionRef.current && formRef.current) {
          actionRef.current?.reset?.();
          setTimeout(() => {
            resetKey();
            if (isSyncToUrl) {
              navigate(currentLocation, { replace: true });
            }
          }, 0);
          // formRef.current.resetFields?.();
          // const newValues = Object.keys(formRef.current?.getFieldsValue())
          //   .map((name) => ({
          //     name,
          //     value: undefined,
          //   }));
          //
          // formRef.current?.setFieldsValue(newValues);
        }
      }}
      columns={newColumns}
      // reload, density and setting are on for every standard table. The
      // keyword search stays opt-in per screen: ProTable would otherwise push
      // its default `keyword` parameter into the request, and which field a
      // table searches is not the wrapper's to decide.
      options={{
        search: false,
        reloadIcon: <HistoryOutlined />,
        ...(options || {}),
        // Any table that turns the toolbar search on gets a clear button with
        // it: once something is typed, there is a way back to the full list
        // that is not "select the text and delete it". Spread after the
        // screen's own options and merged into them, so a screen keeps its
        // placeholder and its onSearch and can still say otherwise.
        ...(options && options.search
          ? {
              search: {
                allowClear: true,
                ...(typeof options.search === "object" ? options.search : {}),
              },
            }
          : {}),
      }}
      search={
        search
          ? {
              labelWidth: "auto",
              filterType: "query",
              searchText: t("common.table.filter"),
              resetText: t("common.table.clear"),
              ...search,
            }
          : false
      }
      // The whole row is the target, not just the icon at its end. Two things
      // inside it are not: the actions cell, whose buttons do their own job,
      // and anything a cell renders as a link or a control — a click there
      // means what that element means. Placed before the prop spread so a
      // screen can still define its own onRow.
      onRow={(record, index) => {
        const open = rowOpen(record, index ?? 0);

        if (!open) {
          return {};
        }

        return {
          style: { cursor: "pointer" },
          onClick: (event: React.MouseEvent<HTMLElement>) => {
            const target = event.target as HTMLElement;

            if (
              target.closest("a, button, input, label, [role='button']") ||
              target.closest(`.${styles.actions}`)
            ) {
              return;
            }

            open(event);
          },
        };
      }}
      // antd shows a "Click to sort" tooltip on every sortable header. The
      // arrows already say the column sorts, and the tooltip followed the
      // cursor across a whole header row. Placed before the prop spread so a
      // screen can still switch it back on.
      showSorterTooltip={false}
      form={{
        syncToUrl: true,
        ...form,
      }}
      {...props}
    />
  );
}

export default Table;
